import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation } from '@tanstack/react-query'
import { observer } from 'mobx-react'
import { useContext } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router'
import z from 'zod'

import { Button } from '@/components/elements/button'
import { Card, CardContent } from '@/components/elements/card'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/elements/form'
import { Input } from '@/components/elements/input'
import { ROUTES } from '@/components/PrivateRoute/PrivateRoute'
import { updateUser } from '@/services/user.service'
import { UserStoreContext } from '@/stores/UserStore'
import { toast } from '@/utils/helpers'

const schema = z.object({
  firstName: z.string().trim().min(1, 'First name is required'),
  lastName: z.string().trim().min(1, 'Last name is required'),
})

const CompleteProfile = observer(() => {
  const { user, setUser } = useContext(UserStoreContext)
  const navigate = useNavigate()

  const form = useForm({
    defaultValues: {
      firstName: user?.firstName ?? '',
      lastName: user?.lastName ?? '',
    },
    resolver: zodResolver(schema),
  })

  const { isPending, mutate } = useMutation({
    mutationFn: (data) => updateUser({ id: user.id, ...data }),
    onError: (err) => toast(err.message, 'error'),
    onSuccess: (data) => {
      setUser(data)
      navigate(ROUTES.ACCOUNTS)
    },
  })

  return (
    <div className="bg-muted flex min-h-svh flex-col items-center justify-center p-6 md:p-10">
      <div className="w-full max-w-sm md:max-w-lg">
        <div className="flex flex-col gap-6">
          <Card className="overflow-hidden p-0">
            <CardContent className="p-0">
              <Form {...form}>
                <form className="p-6 md:p-8" onSubmit={form.handleSubmit((data) => mutate(data))}>
                  <div className="flex flex-col gap-6">
                    <div className="flex flex-col items-center text-center">
                      <h1>Complete your profile</h1>
                      <p className="text-muted-foreground mt-2 text-center text-sm text-balance">
                        Please enter your name to continue.
                      </p>
                    </div>

                    <FormField
                      control={form.control}
                      disabled={isPending}
                      name="firstName"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>First Name</FormLabel>
                          <FormControl>
                            <Input
                              data-testid="firstName"
                              id="firstName"
                              autoComplete="given-name"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      disabled={isPending}
                      name="lastName"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Last Name</FormLabel>
                          <FormControl>
                            <Input
                              data-testid="lastName"
                              id="lastName"
                              autoComplete="family-name"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <Button loading={isPending} type="submit" className="w-full">
                      Continue
                    </Button>
                  </div>
                </form>
              </Form>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
})

export default CompleteProfile
